import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { FileDropDirective } from 'shared/util-common';

import { FileType } from '@treetop/shared/model';

import { selectFile } from '../../../../../util-common/src/lib/file-upload.util';
import { IconDirective } from '../icon/icon.directive';
import { TooltipDirective } from '../tooltip/tooltip.directive';

@Component({
  selector: 'tt-file-drag-drop',
  standalone: true,
  imports: [MatIconModule, TranslocoPipe, IconDirective, TooltipDirective],
  templateUrl: './file-drag-drop.component.html',
  styleUrl: './file-drag-drop.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    {
      directive: FileDropDirective,
      inputs: ['allowedTypes'],
    },
  ],
})
export class FileDragDropComponent {
  private fileDragDropDirective = inject(FileDropDirective);

  @Input() file?: File;
  @Input() allowedTypes: FileType[] = [];
  @Input() info: string;
  @Input() error?: string;
  @Input() showNewVersionLabel = false;

  @Output() fileChanged = new EventEmitter<File>();

  protected invalidExtension = signal<FileType>(null);

  constructor() {
    this.fileDragDropDirective.fileDrop.pipe(takeUntilDestroyed()).subscribe((file) => {
      this.handleFileChanged(file);
    });

    this.fileDragDropDirective.invalidExtension.pipe(takeUntilDestroyed()).subscribe((fileType) => {
      this.handleInvalidExtension(fileType as FileType);
    });
  }

  protected handleFileChanged(file: File) {
    this.file = file;
    this.fileChanged.emit(this.file);
  }

  protected delete() {
    this.file = null;
    this.fileChanged.emit(null);
  }

  protected selectFile() {
    selectFile(this.allowedTypes, (file) => {
      const fileType = file.name.split('.').pop() as FileType;
      if (!this.allowedTypes.includes(fileType)) {
        this.handleInvalidExtension(fileType);
        return;
      }

      this.handleFileChanged(file);
    });
  }

  private handleInvalidExtension(fileType: FileType) {
    this.invalidExtension.set(fileType);
  }
}
